import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderColorPicker } from "pages/secure/facility/websiteBuilder/editor/components/colorPicker/WebsiteBuilderColorPicker";
import { WebsiteBuilderToolbarMenuSections } from "../../../WebsiteBuilderToolbarMenuSections";
import { cloneDeep } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IWebsiteBuilderGlobalStyles } from "redux/reducers/models/websiteBuilder";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { websiteBuilderUpdateGlobalStyles } from "redux/actions/websiteBuilder/websiteBuilder";

export function ColorsView() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const globalStyles = useAppSelector(store => store.websiteBuilderStore.globalStyles);

  const [updatedGlobalStyles, setUpdatedGlobalStyles] = useState<IWebsiteBuilderGlobalStyles>(cloneDeep(globalStyles));

  function dispatchGlobalStyles(
    prevGlobalStyles: IWebsiteBuilderGlobalStyles,
    nextGlobalStyles: IWebsiteBuilderGlobalStyles,
  ) {
    dispatch(
      websiteBuilderUpdateGlobalStyles({
        globalStyles: cloneDeep(nextGlobalStyles),
      }),
    );
  }

  const { handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: globalStyles,
    updatedState: updatedGlobalStyles,
    setUpdatedState: setUpdatedGlobalStyles,
    dispatchState: dispatchGlobalStyles,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.SHORT,
  });

  function renderTextColorsSection() {
    return (
      <>
        {updatedGlobalStyles?.heading && (
          <WebsiteBuilderColorPicker
            title={t("Headings")}
            hexColor={updatedGlobalStyles.heading.color}
            updateHexColor={color =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                heading: {
                  ...prev.heading,
                  color,
                },
              }))
            }
          />
        )}
        {updatedGlobalStyles?.paragraph && (
          <WebsiteBuilderColorPicker
            title={t("Paragraphs")}
            hexColor={updatedGlobalStyles.paragraph.color}
            updateHexColor={color =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                paragraph: {
                  ...prev.paragraph,
                  color,
                },
              }))
            }
          />
        )}
      </>
    );
  }

  function renderButtonColorsSection() {
    if (updatedGlobalStyles?.button) {
      return (
        <>
          <WebsiteBuilderColorPicker
            title={t("Button text")}
            hexColor={updatedGlobalStyles.button.color}
            updateHexColor={color =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                button: {
                  ...prev.button,
                  color,
                },
              }))
            }
          />
          <WebsiteBuilderColorPicker
            title={t("Button background")}
            hexColor={updatedGlobalStyles.button.backgroundColor}
            updateHexColor={backgroundColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                button: {
                  ...prev.button,
                  backgroundColor,
                },
              }))
            }
          />
          <WebsiteBuilderColorPicker
            title={t("Button border")}
            hexColor={updatedGlobalStyles.button.borderColor}
            updateHexColor={borderColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                button: {
                  ...prev.button,
                  borderColor,
                },
              }))
            }
          />
        </>
      );
    }
  }

  return (
    <WebsiteBuilderToolbarMenuSections
      sections={[
        {
          title: t("Text"),
          content: renderTextColorsSection(),
        },
        {
          title: t("Buttons"),
          content: renderButtonColorsSection(),
        },
      ]}
    />
  );
}
